import { formatKr, monthlyCost } from "@/lib/pseo";

export type EditorialBlock = { heading: string; paragraphs: string[] };

export type AmountTier = "micro" | "small" | "medium" | "large" | "xl";

export function amountTier(amount: number): AmountTier {
  if (amount < 10000) return "micro";
  if (amount < 50000) return "small";
  if (amount < 150000) return "medium";
  if (amount < 350000) return "large";
  return "xl";
}

const TIER_PROFILE: Record<
  AmountTier,
  { who: string; rate: [number, number]; years: number; income: string; usage: string }
> = {
  micro: {
    who: "en oväntad utgift som en tandläkarräkning, en trasig vitvara eller en bilreparation",
    rate: [12, 29],
    years: 2,
    income: "runt 15 000 kr per månad före skatt",
    usage: "kortare krediter och kontokrediter",
  },
  small: {
    who: "en resa, en möbelinvestering, en begagnad bil eller att lösa dyra kontokrediter",
    rate: [8, 24],
    years: 5,
    income: "runt 18 000 kr per månad före skatt",
    usage: "mindre renoveringar, bilköp och att samla småkrediter",
  },
  medium: {
    who: "ett bilköp, en badrumsrenovering eller att samla flera dyra lån till ett",
    rate: [6, 19],
    years: 8,
    income: "runt 20 000 kr per månad före skatt",
    usage: "renoveringar, bilköp och samlingslån",
  },
  large: {
    who: "en större renovering, kontantinsats till bostad eller ett samlingslån för hela hushållets krediter",
    rate: [5, 15],
    years: 12,
    income: "runt 25 000 kr per månad före skatt och ofta en stabil anställning",
    usage: "köksrenovering, tillbyggnad och större samlingslån",
  },
  xl: {
    who: "en omfattande renovering, tillbyggnad eller ett samlingslån där flera stora krediter slås ihop",
    rate: [4.5, 12],
    years: 15,
    income: "en hushållsinkomst på minst 35 000 kr per månad, ofta med medsökande",
    usage: "totalrenovering, tillbyggnad och stora samlingslån",
  },
};

function pct(n: number) {
  return n.toString().replace(".", ",") + " %";
}

/**
 * Redaktionellt innehåll som varierar med beloppsnivå och sidans kontext.
 * Målet är 400–600 unika ord per sida utöver den generiska mallen.
 */
export function editorialFor(
  amount: number,
  opts: { context?: string; place?: string } = {},
): EditorialBlock[] {
  const kr = formatKr(amount);
  const t = TIER_PROFILE[amountTier(amount)];
  const ctx = opts.context ? ` ${opts.context}` : "";
  const low = monthlyCost(amount, t.years, t.rate[0]);
  const high = monthlyCost(amount, Math.max(1, Math.round(t.years / 2)), t.rate[1]);
  const totalLow = low * t.years * 12;
  const totalCostLow = totalLow - amount;

  const blocks: EditorialBlock[] = [
    {
      heading: `Vem passar ett lån på ${kr}${ctx}?`,
      paragraphs: [
        `Ett lån på ${kr} tas oftast till ${t.who}. Beloppet ligger i det spann där ett blancolån – alltså ett lån utan säkerhet – normalt är både snabbast och enklast, eftersom du varken behöver pantsätta bostad eller bil. Handläggningen är helt digital och du signerar med BankID, vilket gör att pengarna i de flesta fall finns på kontot inom en till tre bankdagar.`,
        `För att bli beviljad ${kr} vill långivaren normalt se ${t.income}, svensk folkbokföring och att du inte har någon aktiv betalningsanmärkning. Kraven skiljer sig dock kraftigt mellan aktörerna i listan ovan: nischbankerna gör en helhetsbedömning av din ekonomi medan storbankerna tillämpar hårdare schablonregler. Det är också därför räntan för samma person kan skilja tiotals procent mellan två långivare på samma dag.`,
      ],
    },
    {
      heading: `Räntespann och månadskostnad för ${kr}`,
      paragraphs: [
        `För lån i den här storleken ligger räntan i regel mellan ${pct(t.rate[0])} och ${pct(t.rate[1])}. Med den lägsta räntan och ${t.years} års återbetalningstid landar ${kr} på cirka ${formatKr(low)} per månad, medan en kortare löptid på ${Math.max(1, Math.round(t.years / 2))} år och den högsta räntan ger runt ${formatKr(high)} per månad. Skillnaden visar hur mycket både räntan och löptiden påverkar din vardagsekonomi.`,
        `Totalkostnaden är minst lika viktig som månadskostnaden. Vid ${pct(t.rate[0])} ränta och ${t.years} år betalar du tillbaka omkring ${formatKr(totalLow)}, vilket innebär ungefär ${formatKr(totalCostLow)} i ren räntekostnad utöver de ${kr} du lånade. Lägg därtill uppläggningsavgift på 0–695 kr och aviavgift på 0–45 kr per månad, som varierar mellan långivarna i tabellen ovan.`,
      ],
    },
    {
      heading: `Så får du lägst ränta på ${kr}`,
      paragraphs: [
        `Den effektivaste åtgärden är att låta flera banker tävla om din ansökan samtidigt. En låneförmedlare skickar din ansökan till upp till ett trettiotal banker men tar bara en enda kreditupplysning, vilket betyder att du får flera skarpa erbjudanden utan att din kreditvärdighet försämras av upprepade förfrågningar. Att söka direkt hos fem banker var för sig ger fem förfrågningar i din kreditfil och sänker chansen till bra ränta för varje ny ansökan.`,
        `Tre saker sänker räntan mätbart: en medsökande med egen inkomst, att du löser dyra kontokrediter i samma veva, och att du väljer den kortaste löptid du klarar ekonomiskt. Har du redan ett lån till högre ränta bör du också räkna på att lösa det – enligt konsumentkreditlagen får du alltid lösa ett blancolån i förtid utan extra avgift, och räntan slutar löpa samma dag.`,
      ],
    },
    {
      heading: `Vanliga misstag att undvika`,
      paragraphs: [
        `Det vanligaste felet är att bara jämföra månadskostnad. En längre löptid får varje avi att se billig ut samtidigt som totalkostnaden kan bli tiotusentals kronor högre. Jämför alltid effektiv ränta, som inkluderar samtliga avgifter, i stället för nominell ränta. Det andra vanliga misstaget är att låna mer än det man faktiskt behöver bara för att erbjudandet finns – ${t.usage} är rimliga skäl, men buffertlån till konsumtion blir dyrt.`,
        `Tacka heller inte ja till det första budet innan svarstiden gått ut. Ett låneerbjudande gäller normalt i 30 dagar, vilket ger dig tid att jämföra i lugn och ro. Kontrollera slutligen att långivaren har tillstånd hos Finansinspektionen – samtliga aktörer vi listar står under svensk tillsyn.`,
      ],
    },
  ];

  if (opts.place) {
    blocks.splice(1, 0, {
      heading: `Att låna ${kr} i ${opts.place}`,
      paragraphs: [
        `Alla långivare i listan arbetar rikstäckande, vilket innebär att villkoren för ${kr} är desamma i ${opts.place} som i resten av landet. Det som skiljer är din egen ekonomi: boendekostnaden i ${opts.place} vägs in i den så kallade kvar-att-leva-på-kalkylen som varje bank gör innan lånet beviljas. Bor du med hög boendekostnad krävs därför högre inkomst för samma lånebelopp.`,
      ],
    });
  }

  return blocks;
}

/** Extra FAQ-frågor som är unika för beloppsnivån. */
export function tierFaq(amount: number): { q: string; a: string }[] {
  const kr = formatKr(amount);
  const t = TIER_PROFILE[amountTier(amount)];
  return [
    {
      q: `Vilken inkomst krävs för att låna ${kr}?`,
      a: `Långivarna vill normalt se ${t.income}. Beloppet vägs mot dina befintliga lån och din boendekostnad i en kvar-att-leva-på-kalkyl, så en person med låga fasta utgifter kan beviljas ${kr} på lägre inkomst än schablonen.`,
    },
    {
      q: `Hur lång löptid bör jag välja på ${kr}?`,
      a: `För ett lån på ${kr} är ${t.years} år en vanlig maxgräns. Välj den kortaste löptid du klarar – vid ${pct(t.rate[0])} ränta kostar ${kr} cirka ${formatKr(monthlyCost(amount, t.years, t.rate[0]))} per månad på ${t.years} år, men bara ${formatKr(monthlyCost(amount, Math.max(1, Math.round(t.years / 2)), t.rate[0]))} per månad om du halverar tiden – och totalkostnaden sjunker kraftigt.`,
    },
    {
      q: `Kan jag lösa lånet i förtid?`,
      a: `Ja. Enligt konsumentkreditlagen har du alltid rätt att betala tillbaka ett blancolån i förtid utan extra avgift. Räntan slutar löpa samma dag, vilket gör att en extraamortering på ${kr}-lånet alltid lönar sig.`,
    },
    {
      q: `Hur många kreditupplysningar tas om jag jämför?`,
      a: `Ansöker du via en låneförmedlare tas endast en kreditupplysning, oavsett hur många banker som lämnar bud. Söker du direkt hos varje bank registreras däremot en förfrågan per ansökan, vilket sänker din kreditvärdighet tillfälligt.`,
    },
  ];
}
